/**
 * Logique métier des paliers booster (quantités × coefficient par référence).
 * Partagée entre l’étape 2 admin (sélection / personnalisation) et les payloads POST/PATCH.
 */
import {
  apiAdminboostersPostBodyBoosterReferencesItemMinQuantityDefault,
  apiAdminboostersPostBodyBoosterReferencesItemMinQuantityMin
} from '@r/api/generated-zod/index.ts';
import type {
  BoosterReferenceBoosterReadModel,
  BoosterReferenceBoosterWriteModel,
  BoosterReferenceReferenceReadModel
} from '@r/api/generated/client.schemas.ts';

/** Palier de coefficient appliqué à une référence pour une plage de quantités cumulées. */
export interface BoosterReferenceTier {
  minQuantity: number;
  maxQuantity: number | null;
  coefficient: number;
}

export type BoosterReferenceTierMap = Record<number, BoosterReferenceTier[]>;

export const BOOSTER_REFERENCE_DEFAULT_MIN_QUANTITY =
  apiAdminboostersPostBodyBoosterReferencesItemMinQuantityDefault;

const MIN_QUANTITY_FLOOR = apiAdminboostersPostBodyBoosterReferencesItemMinQuantityMin;

/** Palier par défaut : quantités 1 à ∞ avec le coefficient global du booster. */
export function createDefaultReferenceTier(globalCoefficient: number): BoosterReferenceTier {
  return {
    minQuantity: BOOSTER_REFERENCE_DEFAULT_MIN_QUANTITY,
    maxQuantity: null,
    coefficient: globalCoefficient
  };
}

/** Paliers effectifs d’une référence : personnalisés ou palier global par défaut. */
export function getReferenceTiers(
  referenceId: number,
  customTiersByReferenceId: BoosterReferenceTierMap,
  globalCoefficient: number
): BoosterReferenceTier[] {
  return customTiersByReferenceId[referenceId] ?? [createDefaultReferenceTier(globalCoefficient)];
}

/** Compare deux listes de paliers (ordre indifférent). */
export function areTiersEqual(left: BoosterReferenceTier[], right: BoosterReferenceTier[]) {
  if (left.length !== right.length) {
    return false;
  }

  const normalize = (tiers: BoosterReferenceTier[]) =>
    [...tiers]
      .map((tier) => ({
        minQuantity: tier.minQuantity,
        maxQuantity: tier.maxQuantity,
        coefficient: tier.coefficient
      }))
      .sort((a, b) => a.minQuantity - b.minQuantity);

  const normalizedLeft = normalize(left);
  const normalizedRight = normalize(right);

  return (
    normalizedLeft.length === normalizedRight.length &&
    normalizedLeft.every(
      (tier, index) =>
        tier.minQuantity === normalizedRight[index].minQuantity &&
        tier.maxQuantity === normalizedRight[index].maxQuantity &&
        tier.coefficient === normalizedRight[index].coefficient
    )
  );
}

/** Une référence est personnalisée si ses paliers diffèrent du palier global par défaut. */
export function isReferenceCoefficientCustomized(
  referenceId: number,
  customTiersByReferenceId: BoosterReferenceTierMap,
  globalCoefficient: number
) {
  const tiers = customTiersByReferenceId[referenceId];
  if (!tiers) {
    return false;
  }

  return !areTiersEqual(tiers, [createDefaultReferenceTier(globalCoefficient)]);
}

/** Retire la personnalisation si les paliers équivalent au coefficient global seul. */
export function normalizeCustomTiers(
  tiers: BoosterReferenceTier[],
  globalCoefficient: number
): BoosterReferenceTier[] | undefined {
  const normalized = tiers.map((tier) => ({
    minQuantity: tier.minQuantity,
    maxQuantity: tier.maxQuantity,
    coefficient: tier.coefficient
  }));

  if (areTiersEqual(normalized, [createDefaultReferenceTier(globalCoefficient)])) {
    return undefined;
  }

  return normalized;
}

/** Convertit la réponse GET booster en payload write (IRIs + paliers). */
export function boosterReferencesReadToWriteModel(
  boosterReferences: BoosterReferenceBoosterReadModel[] | undefined
): BoosterReferenceBoosterWriteModel[] {
  return (boosterReferences ?? []).map((entry) => ({
    reference: `/references/${entry.reference.id}`,
    minQuantity: entry.minQuantity ?? BOOSTER_REFERENCE_DEFAULT_MIN_QUANTITY,
    maxQuantity: entry.maxQuantity ?? null,
    coefficient: entry.coefficient
  }));
}

/** Construit le tableau `boosterReferences` envoyé à l’API pour les refs sélectionnées. */
export function buildBoosterReferencesWriteModel(
  selectedReferenceIds: number[],
  customTiersByReferenceId: BoosterReferenceTierMap,
  globalCoefficient: number
): BoosterReferenceBoosterWriteModel[] {
  const sortedReferenceIds = [...selectedReferenceIds].sort((left, right) => left - right);

  return sortedReferenceIds.flatMap((referenceId) =>
    getReferenceTiers(referenceId, customTiersByReferenceId, globalCoefficient).map((tier) => ({
      reference: `/references/${referenceId}`,
      minQuantity: tier.minQuantity,
      maxQuantity: tier.maxQuantity,
      coefficient: tier.coefficient
    }))
  );
}

function normalizeWriteModelForComparison(entries: BoosterReferenceBoosterWriteModel[]) {
  return [...entries]
    .map((entry) => ({
      reference: entry.reference,
      minQuantity: entry.minQuantity ?? BOOSTER_REFERENCE_DEFAULT_MIN_QUANTITY,
      maxQuantity: entry.maxQuantity ?? null,
      coefficient: entry.coefficient
    }))
    .sort((left, right) => {
      const referenceCompare = left.reference.localeCompare(right.reference);
      if (referenceCompare !== 0) {
        return referenceCompare;
      }

      return left.minQuantity - right.minQuantity;
    });
}

/** Détecte un changement de sélection ou de paliers pour le PATCH partiel. */
export function areBoosterReferencesWriteModelsEqual(
  left: BoosterReferenceBoosterWriteModel[],
  right: BoosterReferenceBoosterWriteModel[]
) {
  const normalizedLeft = normalizeWriteModelForComparison(left);
  const normalizedRight = normalizeWriteModelForComparison(right);

  if (normalizedLeft.length !== normalizedRight.length) {
    return false;
  }

  return normalizedLeft.every(
    (entry, index) =>
      entry.reference === normalizedRight[index]?.reference &&
      entry.minQuantity === normalizedRight[index]?.minQuantity &&
      entry.maxQuantity === normalizedRight[index]?.maxQuantity &&
      entry.coefficient === normalizedRight[index]?.coefficient
  );
}

/** Déduit le coefficient global affiché à l'étape 1 à partir des paliers API. */
export function inferGlobalCoefficientFromBoosterReferences(
  boosterReferences?: BoosterReferenceBoosterReadModel[]
): number {
  if (!boosterReferences?.length) {
    return 2;
  }

  const defaultTier = boosterReferences.find(
    (entry) =>
      (entry.minQuantity ?? BOOSTER_REFERENCE_DEFAULT_MIN_QUANTITY) ===
        BOOSTER_REFERENCE_DEFAULT_MIN_QUANTITY && (entry.maxQuantity ?? null) === null
  );

  return defaultTier?.coefficient ?? boosterReferences[0]?.coefficient ?? 2;
}

/** Rehydrate la map de paliers personnalisés depuis un booster existant (édition). */
export function boosterReferencesToCustomTierMap(
  boosterReferences: BoosterReferenceBoosterReadModel[] | undefined,
  globalCoefficient: number
): BoosterReferenceTierMap {
  const tiersByReferenceId = new Map<number, BoosterReferenceTier[]>();

  for (const entry of boosterReferences ?? []) {
    const referenceId = entry.reference.id;
    const tier: BoosterReferenceTier = {
      minQuantity: entry.minQuantity ?? BOOSTER_REFERENCE_DEFAULT_MIN_QUANTITY,
      maxQuantity: entry.maxQuantity ?? null,
      coefficient: entry.coefficient
    };

    const existing = tiersByReferenceId.get(referenceId) ?? [];
    existing.push(tier);
    tiersByReferenceId.set(referenceId, existing);
  }

  const customMap: BoosterReferenceTierMap = {};

  for (const [referenceId, tiers] of tiersByReferenceId) {
    const normalized = normalizeCustomTiers(tiers, globalCoefficient);
    if (normalized) {
      customMap[referenceId] = normalized;
    }
  }

  return customMap;
}

/** Liste dédupliquée des références produit à partir des entrées `boosterReferences`. */
export function uniqueReferencesFromBoosterReferences(
  boosterReferences: BoosterReferenceBoosterReadModel[] | undefined
) {
  const referencesById = new Map<number, BoosterReferenceBoosterReadModel['reference']>();

  for (const entry of boosterReferences ?? []) {
    referencesById.set(entry.reference.id, entry.reference);
  }

  return [...referencesById.values()];
}

/** Résumé texte des coefficients (conflits / tooltips) quand plusieurs paliers coexistent. */
export function formatBoosterTiersCoefficientSummary(
  tiers?: readonly BoosterReferenceReferenceReadModel[]
) {
  if (!tiers?.length) {
    return null;
  }

  const coefficients = [...new Set(tiers.map((tier) => tier.coefficient))];

  if (coefficients.length === 1) {
    return coefficients[0];
  }

  return coefficients.join(' / ');
}

/** Valide chevauchements et bornes des paliers saisis dans la modale admin. */
export function validateReferenceTiers(
  tiers: BoosterReferenceTier[],
  t: (key: string) => string
): string | null {
  if (tiers.length === 0) {
    return t('boosters.create.references.tiers.required');
  }

  const sortedTiers = [...tiers].sort((left, right) => left.minQuantity - right.minQuantity);

  for (const tier of sortedTiers) {
    if (!Number.isInteger(tier.minQuantity) || tier.minQuantity < MIN_QUANTITY_FLOOR) {
      return t('boosters.create.references.tiers.min.invalid');
    }

    if (tier.maxQuantity != null) {
      if (!Number.isInteger(tier.maxQuantity) || tier.maxQuantity < MIN_QUANTITY_FLOOR) {
        return t('boosters.create.references.tiers.max.invalid');
      }

      if (tier.maxQuantity < tier.minQuantity) {
        return t('boosters.create.references.tiers.range.invalid');
      }
    }

    if (tier.coefficient < 0 || !Number.isFinite(tier.coefficient)) {
      return t('boosters.create.references.tiers.coefficient.invalid');
    }
  }

  for (let index = 1; index < sortedTiers.length; index += 1) {
    const previous = sortedTiers[index - 1];
    const current = sortedTiers[index];

    if (previous.maxQuantity == null) {
      return t('boosters.create.references.tiers.overlap');
    }

    if (current.minQuantity <= previous.maxQuantity) {
      return t('boosters.create.references.tiers.overlap');
    }
  }

  return null;
}
